import React, { useMemo, useState } from "react";
import { Col, Container, Form, Row, Table } from "react-bootstrap";
import CurrencyFormat from "react-currency-format";
import styled from "styled-components";
import LogButton from "../../../../../../../components/atoms/LogButton";
import LogDateInput from "../../../../../../../components/atoms/LogDateInput";
import LogTextArea from "../../../../../../../components/atoms/LogTextArea";
import {
  postCreditNote,
  postInvoiceCleanUp,
} from "../../../../../../../api/modules";
import {
  errorToast,
  successToast,
} from "../../../../../../../services/toastService";
import { useFormik } from "formik";

const ItemsPreviewContainer = styled.div`
  max-height: 300px;
  overflow: auto;

  textarea {
    min-height: 72px;
    resize: vertical;
  }

  .form-control-sm {
    font-size: 0.82rem;
  }
`;

const FACTURANTE_MEASURE_UNITS = [
  { value: 1, label: "unidades" },
  { value: 2, label: "kilogramos" },
  { value: 19, label: "toneladas" },
  { value: 44, label: "otras unidades" },
];

const FACTURANTE_VATS = [
  { value: 1, label: "NG", rate: 0 },
  { value: 2, label: "Exento", rate: 0 },
  { value: 3, label: "0%", rate: 0 },
  { value: 4, label: "2.5%", rate: 2.5 },
  { value: 5, label: "5%", rate: 5 },
  { value: 6, label: "10.5%", rate: 10.5 },
  { value: 7, label: "21%", rate: 21 },
  { value: 8, label: "27%", rate: 27 },
];

const roundNumber = (value, decimals = 2) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  const factor = 10 ** decimals;
  return Math.round((number + Number.EPSILON) * factor) / factor;
};

const parseDecimalInput = (value) => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return Number(value);
  return Number(value.replace(/\./g, "").replace(",", "."));
};

const getFiniteDecimal = (value) => {
  const number = parseDecimalInput(value);
  return Number.isFinite(number) ? number : 0;
};

const getVatRate = (vatValue) =>
  FACTURANTE_VATS.find((vat) => vat.value === Number(vatValue))?.rate || 0;

const getItemsTotal = (items = []) =>
  roundNumber(
    items.reduce((total, item) => {
      const quantity = getFiniteDecimal(item.quantity);
      const unitPrice = getFiniteDecimal(item.unit_price);
      return total + quantity * unitPrice * (1 + getVatRate(item.vat) / 100);
    }, 0),
    2
  );

const getTripCode = (data) => `${data?.parent_id || ""}${data?.order || ""}`;

const isErrorCreditNote = (creditNote) =>
  ["error", "unauthorized"].includes(creditNote?.status);

const buildDefaultItem = (data) => {
  const tripCode = getTripCode(data);
  return {
    description: [
      `Nota de credito parcial por merma de kilos (Viaje ID${tripCode})`,
      `Remito / CP / CTG: ${data?.shipping_note || "-"}`,
    ].join("\n"),
    quantity: 1,
    unit_price: "",
    measure_unit: 1,
    vat: 7,
    code: `NC${tripCode || Date.now()}`,
    trip_detail_id: data?._id,
  };
};

const buildEmptyItem = (index) => ({
  description: "",
  quantity: 1,
  unit_price: "",
  measure_unit: 1,
  vat: 7,
  code: `NCITEM${index + 1}`,
});

const CreditNoteSubmit = ({ data, refetch }) => {
  const [cleaningId, setCleaningId] = useState(null);
  const invoice = data?.invoice_id;
  const canCreate = invoice?.status === "authorized" && invoice?.invoice_number;
  const errorCreditNotes = (invoice?.credit_notes || []).filter(
    isErrorCreditNote
  );
  const initialValues = useMemo(
    () => {
      const invoiceRows = data?.invoice_trip_rows?.length
        ? data.invoice_trip_rows
        : [data].filter(Boolean);

      return {
        credit_note_date: new Date(),
        observation: "",
        custom_items: invoiceRows.map(buildDefaultItem),
      };
    },
    [data]
  );

  const {
    values,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    resetForm,
  } = useFormik({
    enableReinitialize: true,
    initialValues,
    validate: (values) => {
      const validationErrors = {};
      if (!values.credit_note_date) {
        validationErrors.credit_note_date = "Fecha obligatoria";
      }

      const invoiceDate = invoice?.invoice_date || invoice?.date;
      if (values.credit_note_date && invoiceDate) {
        const creditNoteDate = new Date(values.credit_note_date);
        const originalInvoiceDate = new Date(invoiceDate);

        creditNoteDate.setHours(0, 0, 0, 0);
        originalInvoiceDate.setHours(0, 0, 0, 0);

        if (creditNoteDate < originalInvoiceDate) {
          validationErrors.credit_note_date =
            "La fecha no puede ser anterior a la factura.";
        }
      }

      if (getItemsTotal(values.custom_items) <= 0) {
        validationErrors.custom_items_total =
          "El total de la nota debe ser mayor a cero";
      }

      values.custom_items.forEach((item, index) => {
        const description = item.description?.trim();
        const quantity = getFiniteDecimal(item.quantity);
        const unitPrice = getFiniteDecimal(item.unit_price);
        const measureUnit = Number(item.measure_unit);
        const vat = Number(item.vat ?? 7);

        if (!description) {
          validationErrors[`custom_items.${index}.description`] =
            "Descripcion obligatoria";
        }
        if (!Number.isFinite(quantity) || quantity <= 0) {
          validationErrors[`custom_items.${index}.quantity`] =
            "Cantidad invalida";
        }
        if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
          validationErrors[`custom_items.${index}.unit_price`] =
            "Precio invalido";
        }
        if (
          !FACTURANTE_MEASURE_UNITS.some((unit) => unit.value === measureUnit)
        ) {
          validationErrors[`custom_items.${index}.measure_unit`] =
            "Unidad invalida";
        }
        if (!FACTURANTE_VATS.some((itemVat) => itemVat.value === vat)) {
          validationErrors[`custom_items.${index}.vat`] = "IVA invalido";
        }
      });

      return validationErrors;
    },
    onSubmit: async (values) => {
      if (!canCreate) {
        errorToast("La factura debe estar autorizada para generar una NC.");
        return;
      }

      const normalizedItems = values.custom_items.map((item) => ({
        description: item.description?.trim(),
        quantity: getFiniteDecimal(item.quantity),
        unit_price: getFiniteDecimal(item.unit_price),
        measure_unit: Number(item.measure_unit),
        vat: Number(item.vat),
        code: item.code,
      }));
      const selectedTripDetails = [
        ...new Set(
          values.custom_items
            .map((item) => item.trip_detail_id)
            .filter(Boolean)
        ),
      ];

      const resp = await postCreditNote({
        invoice_id: invoice?._id,
        trip_details: selectedTripDetails,
        credit_note_date: values.credit_note_date,
        observation: values.observation?.trim(),
        custom_items: normalizedItems,
      });

      if (resp?.msg) {
        errorToast(resp.msg || "Intente nuevamente");
        return;
      }

      successToast("Nota de credito solicitada con exito");
      resetForm();
      refetch();
    },
  });

  const cleanCreditNote = async (creditNote) => {
    if (!creditNote?._id) return;
    setCleaningId(creditNote._id);

    try {
      const { msg } = await postInvoiceCleanUp({
        _id: creditNote._id,
        type: "creditNote",
      });

      if (msg) {
        errorToast(msg || "Intente nuevamente");
        return;
      }

      successToast("Nota de credito limpia, ya puede reintentar.");
      refetch();
    } finally {
      setCleaningId(null);
    }
  };

  const updateCustomItem = (index, key, value) => {
    const nextItems = [...(values.custom_items || [])];
    nextItems[index] = {
      ...nextItems[index],
      [key]: value,
    };
    setFieldValue("custom_items", nextItems);
  };

  const addCustomItem = () => {
    const currentItems = values.custom_items || [];
    setFieldValue("custom_items", [
      ...currentItems,
      buildEmptyItem(currentItems.length),
    ]);
  };

  const removeCustomItem = (index) => {
    const currentItems = values.custom_items || [];
    if (currentItems.length <= 1) return;

    setFieldValue(
      "custom_items",
      currentItems.filter((_, itemIndex) => itemIndex !== index)
    );
  };

  const normalizeQuantityOnBlur = (index) => {
    const item = values.custom_items?.[index];
    if (!item || Number(item.measure_unit) !== 19) return;

    const quantity = parseDecimalInput(item.quantity);
    if (!Number.isFinite(quantity)) return;

    updateCustomItem(index, "quantity", roundNumber(quantity, 2).toFixed(2));
  };

  const totalItems = getItemsTotal(values.custom_items);

  return (
    <Container fluid style={{ maxWidth: 900 }}>
      <Row>
        <Col>
          <h1 className="h5 mt-3 mb-4 text-primary text-center font-weight-bold">
            Nota de credito sobre {invoice?.invoice_number || "factura"}
          </h1>
        </Col>
      </Row>
      {errorCreditNotes.length ? (
        <Row className="mb-3">
          <Col>
            <div className="border rounded p-3">
              <p className="font-weight-bold text-danger mb-2">
                Notas de credito con error
              </p>
              {errorCreditNotes.map((creditNote) => (
                <div
                  key={creditNote._id}
                  className="d-flex justify-content-between align-items-center mb-2"
                >
                  <span>
                    {creditNote.number || "Pendiente"} -{" "}
                    {creditNote.status || "error"}
                  </span>
                  <button
                    type="button"
                    className="btn btn-link p-0 text-danger"
                    onClick={() => cleanCreditNote(creditNote)}
                    disabled={cleaningId === creditNote._id}
                  >
                    {cleaningId === creditNote._id
                      ? "Limpiando..."
                      : "Limpiar error"}
                  </button>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      ) : null}

      {!canCreate ? (
        <Row>
          <Col>
            <p className="text-center mb-0">
              La factura debe estar autorizada para generar una nota de credito.
            </p>
          </Col>
        </Row>
      ) : (
        <Form.Group>
          <Row className="mb-3">
            <Col xs={4}>
              <label className="black-primary font-weight-bold mb-0 mt-2">
                Fecha comprobante:
              </label>
            </Col>
            <Col xs={8}>
              <LogDateInput
                size="md"
                name="credit_note_date"
                className="number-font p-0"
                placeholder="Fecha de comprobante"
                onBlur={handleBlur.credit_note_date}
                errors={errors}
                value={values.credit_note_date}
                onChange={(value) => {
                  setFieldValue("credit_note_date", value);
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <label className="black-primary font-weight-bold mr-2">
                Cuit Dador:
              </label>
              <span>{data?.id_dador?.cuit || invoice?.id_dador?.cuit}</span>
            </Col>
          </Row>
          <Row>
            <Col>
              <label className="black-primary font-weight-bold mr-2">
                Factura asociada:
              </label>
              <span>{invoice?.invoice_number}</span>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <LogTextArea
                placeholder="Observacion nota de credito"
                name="observation"
                value={values.observation}
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="black-primary font-weight-bold mb-0">
                  Items a acreditar
                </label>
                <small className="text-muted">
                  Se envia a Facturante con el mismo punto de venta de la factura.
                </small>
              </div>
              <ItemsPreviewContainer>
                <Table bordered size="sm" responsive className="mb-0">
                  <thead>
                    <tr>
                      <th>Descripcion</th>
                      <th className="text-center">Cantidad</th>
                      <th className="text-center">Unidad</th>
                      <th className="text-center">Precio unit.</th>
                      <th className="text-center">IVA</th>
                      <th className="text-right">Total</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {values.custom_items?.map((item, index) => {
                      const itemTotal =
                        getFiniteDecimal(item.quantity) *
                        getFiniteDecimal(item.unit_price) *
                        (1 + getVatRate(item.vat) / 100);

                      return (
                        <tr key={`${item.code}-${index}`}>
                          <td style={{ minWidth: 280 }}>
                            <Form.Control
                              as="textarea"
                              size="sm"
                              value={item.description}
                              onChange={(event) =>
                                updateCustomItem(
                                  index,
                                  "description",
                                  event.target.value
                                )
                              }
                            />
                            {errors[`custom_items.${index}.description`] ? (
                              <small className="text-danger">
                                {errors[`custom_items.${index}.description`]}
                              </small>
                            ) : null}
                          </td>
                          <td style={{ width: 95 }}>
                            <Form.Control
                              type="text"
                              inputMode="decimal"
                              size="sm"
                              value={item.quantity}
                              onChange={(event) =>
                                updateCustomItem(
                                  index,
                                  "quantity",
                                  event.target.value
                                )
                              }
                              onBlur={() => normalizeQuantityOnBlur(index)}
                            />
                            {errors[`custom_items.${index}.quantity`] ? (
                              <small className="text-danger">
                                {errors[`custom_items.${index}.quantity`]}
                              </small>
                            ) : null}
                          </td>
                          <td style={{ width: 120 }}>
                            <Form.Control
                              as="select"
                              size="sm"
                              value={item.measure_unit || 1}
                              onChange={(event) =>
                                updateCustomItem(
                                  index,
                                  "measure_unit",
                                  Number(event.target.value)
                                )
                              }
                            >
                              {FACTURANTE_MEASURE_UNITS.map((unit) => (
                                <option key={unit.value} value={unit.value}>
                                  {unit.label}
                                </option>
                              ))}
                            </Form.Control>
                            {errors[`custom_items.${index}.measure_unit`] ? (
                              <small className="text-danger d-block">
                                {errors[`custom_items.${index}.measure_unit`]}
                              </small>
                            ) : null}
                          </td>
                          <td style={{ width: 135 }}>
                            <Form.Control
                              type="text"
                              inputMode="decimal"
                              size="sm"
                              value={item.unit_price}
                              onChange={(event) =>
                                updateCustomItem(
                                  index,
                                  "unit_price",
                                  event.target.value
                                )
                              }
                            />
                            {errors[`custom_items.${index}.unit_price`] ? (
                              <small className="text-danger">
                                {errors[`custom_items.${index}.unit_price`]}
                              </small>
                            ) : null}
                          </td>
                          <td style={{ width: 105 }}>
                            <Form.Control
                              as="select"
                              size="sm"
                              value={item.vat ?? 7}
                              onChange={(event) =>
                                updateCustomItem(
                                  index,
                                  "vat",
                                  Number(event.target.value)
                                )
                              }
                            >
                              {FACTURANTE_VATS.map((vat) => (
                                <option key={vat.value} value={vat.value}>
                                  {vat.label}
                                </option>
                              ))}
                            </Form.Control>
                            {errors[`custom_items.${index}.vat`] ? (
                              <small className="text-danger">
                                {errors[`custom_items.${index}.vat`]}
                              </small>
                            ) : null}
                          </td>
                          <td className="text-right" style={{ width: 120 }}>
                            <CurrencyFormat
                              value={itemTotal}
                              displayType={"text"}
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              prefix={"$"}
                              renderText={(formattedValue) => (
                                <span>{formattedValue}</span>
                              )}
                            />
                          </td>
                          <td className="text-center" style={{ width: 40 }}>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-danger"
                              onClick={() => removeCustomItem(index)}
                              disabled={values.custom_items?.length <= 1}
                            >
                              x
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </ItemsPreviewContainer>
              {errors.custom_items_total ? (
                <small className="text-danger d-block mt-1">
                  {errors.custom_items_total}
                </small>
              ) : null}
              <div className="d-flex justify-content-between align-items-center mt-2">
                <button
                  type="button"
                  className="btn btn-link p-0 text-primary"
                  onClick={addCustomItem}
                >
                  Agregar item
                </button>
                <div>
                  <span className="font-weight-bold mr-2">Total NC:</span>
                  <CurrencyFormat
                    value={totalItems}
                    displayType={"text"}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    prefix={"$"}
                  />
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col className="d-flex justify-content-center">
              <LogButton
                disabled={isSubmitting || totalItems <= 0}
                className="w-50"
                color="primary"
                onClick={handleSubmit}
                title={isSubmitting ? "Generando..." : "Generar nota de credito"}
                border="curve"
              />
            </Col>
          </Row>
        </Form.Group>
      )}
    </Container>
  );
};

export default CreditNoteSubmit;
